/* eslint-disable react/prop-types */
/* eslint-disable react/no-unstable-nested-components */
import { Modal } from "antd";
import { isBefore } from "date-fns";
import { useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import { useTable } from "react-table";
import BackupForm from "../../components/BackupForm";
import Loader from "../../components/Loader";
import MembersButton from "../../components/MembersButton";
import httpClient from "../../datasources/httpClient";
import useAuth from "../../hooks/useAuth";
import useBackups from "../../hooks/useBackups";
import useWorkspaces from "../../hooks/useWorkspaces";
import { formatRelativeBr } from "../../utils/format";
import resultIsSuccess from "../../utils/resultIsSuccess";
import translateResult from "../../utils/translateResult";
import {
  Container,
  Header,
  IconButton,
  MarginTop,
  NextReportTableData,
  Pre,
  ResultTableData,
  Row,
  THeader,
  Table,
  TableBody,
  WorkspaceTHeader,
} from "./styles";

export default function HomePage() {
  const { workspaces, isLoading: isLoadingWorkspaces } = useWorkspaces();
  const { backups, isLoading: isLoadingBackups } = useBackups();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [openConfig, setOpenConfig] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [selectedRowId, setSelectedRowId] = useState("");

  const isLoading = isLoadingWorkspaces || isLoadingBackups;

  async function handleBackupDeletion({ id }) {
    await httpClient.delete("backups", {
      data: { ids: [id] },
    });

    queryClient.invalidateQueries("backups");
  }

  async function handleFetchBackupConfiguration({ id }) {
    if (id) {
      try {
        setLoading(true);
        setOpenConfig(true);
        const response = await httpClient.get(`backups-config/${id}`);

        if (response.status === 200) {
          setModalContent(response.data);
        }
      } catch (error) {
        setOpenConfig(false);
      } finally {
        setLoading(false);
      }
    }
  }

  function handleClose() {
    setSelectedRowId(null);
    setOpenConfig(false);
    setOpenDelete(false);
    setOpenEdit(false);
  }

  function handleCopy() {
    setOpenConfig(false);
    navigator.clipboard.writeText(JSON.stringify(modalContent));
  }

  const columns = useMemo(
    () => [
      {
        Header: "Nome",
        accessor: "name",
        Cell: ({ row, value }) => <a href={`/reports/${row.id}`}>{value}</a>,
        // minWidth: '260px',
      },
      { Header: "Tamanho", accessor: "size" },
      { Header: "Próximo relatório", accessor: "nextReportExpectedAt" },
      { Header: "Último Backup Bem-Sucedido", accessor: "lastSuccefulReport" },
      { Header: "Último Relatório", accessor: "lastReport" },
      {
        Header: "",
        id: "actions",
        accessor: "",
        Cell: ({ row }) => {
          const backup = backups.find((bkp) => bkp.id === row.id);
          const workspace = workspaces.find(
            (wks) => wks.id === backup?.workspaceId
          );

          const member = workspace?.members.find(
            ({ email }) => email === user.email
          );

          if (member) {
            return (
              <Row>
                <div>
                  <IconButton
                    title="Get selected rows config"
                    type="button"
                    onClick={() =>
                      handleFetchBackupConfiguration({ id: row.id })
                    }
                  >
                    <span className="material-symbols-outlined grey">
                      settings
                    </span>
                  </IconButton>
                </div>
                {member.role.name === "admin" ? (
                  <>
                    <IconButton
                      title="Edit row"
                      type="button"
                      onClick={() => {
                        setSelectedRowId(row.id);
                        setOpenEdit(true);
                      }}
                    >
                      <span className="material-symbols-outlined grey">
                        edit
                      </span>
                    </IconButton>
                    <div>
                      <IconButton
                        title="Delete selected rows"
                        type="button"
                        onClick={() => {
                          setSelectedRowId(row.id);
                          setOpenDelete(true);
                        }}
                      >
                        <span className="material-symbols-outlined">
                          delete
                        </span>
                      </IconButton>
                    </div>
                  </>
                ) : null}
              </Row>
            );
          }

          return <div />;
        },
      },
    ],
    [workspaces, backups]
  );

  const data = useMemo(
    () =>
      backups
        .sort(({ nextReportExpectedAt: a }, { nextReportExpectedAt: b }) => {
          if (!a && !b) {
            return Date.now() - Date.now();
          }
          if (!a) {
            return Date.now() - new Date(b);
          }
          if (!b) {
            return new Date(a) - Date.now();
          }

          return new Date(a) - new Date(b);
        })
        .map((backup) => {
          if (backup.reports.length === 0) {
            return {
              name: backup.name,
              id: backup.id,
            };
          }

          const lastSuccefulReport = backup.reports.find((report) =>
            resultIsSuccess({ value: report.result })
          );

          const lastReport = backup.reports[0];
          const lastReportResult = translateResult(lastReport.result);
          const now = Date.now();

          return {
            name: backup.name,
            size: `${(parseInt(lastReport.size, 10) / (1024 * 1024)).toFixed(
              2
            )} MB`,
            nextReportExpectedAt: formatRelativeBr(
              new Date(backup.nextReportExpectedAt),
              now
            ),
            lastSuccefulReport: lastSuccefulReport
              ? `${formatRelativeBr(new Date(lastSuccefulReport.date), now)}`
              : null,
            lastReport: `${lastReportResult} ${formatRelativeBr(
              new Date(lastReport.date),
              now
            )}`,
            id: backup.id,
            isLate: isBefore(new Date(backup.nextReportExpectedAt), Date.now()),
          };
        }),
    [backups]
  );

  const getRowId = (row, relativeIndex, parent) =>
    parent ? [parent.id, row.id].join(".") : row.id;

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data, getRowId });

  return (
    <Container>
      <Header>
        <h1>Meus Workspaces</h1>
        <Link to="/new-workspace">Novo Workspace</Link>
      </Header>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <Modal
            centered
            open={openEdit}
            footer={[]}
            onCancel={() => handleClose()}
          >
            <MarginTop>
              <BackupForm backupId={selectedRowId} />
            </MarginTop>
          </Modal>
          <Modal
            centered
            open={openDelete}
            onCancel={() => handleClose()}
            onOk={() => handleBackupDeletion({ id: selectedRowId })}
            title="Confirmação de exclusão"
            okText="Deletar"
            cancelText="Cancelar"
            okType="danger"
          >
            Tem certeza que deseja deletar o(s) backup(s) selecionado(s)?
          </Modal>
          <Modal
            centered
            open={openConfig}
            onCancel={() => handleClose()}
            onOk={() => handleCopy()}
            okText="Copiar"
            cancelText="Cancelar"
          >
            {loading ? (
              <Loader />
            ) : (
              <Pre data-lang="JSON">
                <code>{JSON.stringify(modalContent)}</code>
              </Pre>
            )}
          </Modal>
          <Table {...getTableProps()}>
            {workspaces.map((workspace) => {
              const rowsIds = backups
                .filter((bkp) => bkp.workspaceId === workspace.id)
                .map((bkp) => bkp.id);

              const workspaceRows = rows.filter((row) =>
                rowsIds.includes(row.id)
              );

              return (
                <>
                  <thead key={`Head${workspace.id}`}>
                    <tr>
                      <WorkspaceTHeader>
                        {workspace.name}
                        <MembersButton workspace={workspace} />
                      </WorkspaceTHeader>
                    </tr>
                    {headerGroups.map((headerGroup) => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                          <THeader {...column.getHeaderProps()}>
                            {column.render("Header")}
                          </THeader>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <TableBody
                    key={`Body${workspace.id}`}
                    {...getTableBodyProps()}
                  >
                    {workspaceRows.map((row) => {
                      prepareRow(row);

                      return (
                        <tr {...row.getRowProps()}>
                          {row.cells.map((cell) => {
                            if (cell.column.Header === "Último Relatório") {
                              const isSuccess =
                                cell.value?.includes("Bem-sucedido");
                              const isWarning = cell.value?.includes("Alerta");

                              return (
                                <ResultTableData
                                  isSuccess={isSuccess}
                                  isWarning={isWarning}
                                  {...cell.getCellProps()}
                                >
                                  {cell.render("Cell")}
                                </ResultTableData>
                              );
                            }

                            if (cell.column.Header.includes("Próx")) {
                              const dataRow = data.find(
                                (dt) => dt.id === row.id
                              );

                              return (
                                <NextReportTableData
                                  isLate={dataRow.isLate}
                                  {...cell.getCellProps()}
                                >
                                  {cell.render("Cell")}
                                </NextReportTableData>
                              );
                            }

                            return (
                              <td
                                {...cell.getCellProps({
                                  style: {
                                    minWidth: cell.column.minWidth,
                                    width: cell.column.width,
                                  },
                                })}
                              >
                                {cell.render("Cell")}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </TableBody>
                </>
              );
            })}
          </Table>
        </>
      )}
    </Container>
  );
}
